import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { buildAnomalyFallback } from '../../utils_1/forecastUtils';

const toNumber = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLabel = (value, index, prefix = 'Point') => {
  if (value == null) return `${prefix} ${index + 1}`;
  const text = String(value).trim();
  return text.length > 0 ? text : `${prefix} ${index + 1}`;
};

const padToLength = (values, length, fill = 0) => {
  const out = Array.isArray(values) ? values.slice() : [];
  while (out.length < length) out.push(fill);
  return out.slice(0, length);
};

const median = (values) => {
  const nums = (Array.isArray(values) ? values : [])
    .map((value) => toNumber(value))
    .filter((value) => value != null)
    .sort((a, b) => a - b);
  if (nums.length === 0) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 === 0 ? (nums[mid - 1] + nums[mid]) / 2 : nums[mid];
};

const buildSpikeFallback = (seriesValues, baseValues = [], promoValues = [], historicalSummary = {}) =>
  buildAnomalyFallback(seriesValues, baseValues, promoValues, historicalSummary);

const buildXYSeries = (labels, values) => {
  return labels.map((label, index) => ({
    x: label,
    y: toNumber(values?.[index]) ?? 0,
  }));
};

const buildConfidenceBand = (labels, totalSeries, upperInput, lowerInput) => {
  const length = Array.isArray(labels) ? labels.length : 0;
  const total = padToLength(totalSeries, length, 0);
  const fallbackUpper = total.map((value) => Math.round((toNumber(value) ?? 0) * 1.1));
  const fallbackLower = total.map((value) => Math.round((toNumber(value) ?? 0) * 0.9));

  const upper = Array.isArray(upperInput) && upperInput.length === length
    ? upperInput.map((value, index) => toNumber(value) ?? fallbackUpper[index] ?? 0)
    : fallbackUpper;
  const lower = Array.isArray(lowerInput) && lowerInput.length === length
    ? lowerInput.map((value, index) => toNumber(value) ?? fallbackLower[index] ?? 0)
    : fallbackLower;

  return Array.from({ length }, (_, index) => {
    const low = Math.min(lower[index] ?? 0, upper[index] ?? 0);
    const high = Math.max(lower[index] ?? 0, upper[index] ?? 0);
    return {
      x: labels[index],
      y: [Math.max(0, low), Math.max(0, high)],
    };
  });
};

const formatTooltipValue = (value) => {
  if (Array.isArray(value)) {
    const low = toNumber(value[0]);
    const high = toNumber(value[1]);
    const safeLow = low == null ? 0 : Math.max(0, low);
    const safeHigh = high == null ? 0 : Math.max(0, high);
    return `${safeLow >= 1000 ? `${(safeLow / 1000).toFixed(1)}K` : Math.round(safeLow).toLocaleString()} - ${safeHigh >= 1000 ? `${(safeHigh / 1000).toFixed(1)}K` : Math.round(safeHigh).toLocaleString()}`;
  }
  const num = toNumber(value);
  const safe = num == null ? 0 : num;
  return safe >= 1000 ? `${(safe / 1000).toFixed(1)}K` : Math.round(safe).toLocaleString();
};

const isWeakAnomalyReason = (reason) => {
  const text = String(reason || '').trim().toLowerCase();
  if (!text) return true;
  return [
    'historical outlier',
    'local trend',
    'extreme deviation',
    'statistical outlier',
    'historical pattern',
  ].some((phrase) => text.includes(phrase));
};

const safeSeriesValue = (values, index, fallback = 0) => {
  const num = toNumber(values?.[index]);
  return Number.isFinite(num) ? num : fallback;
};

/**
 * DailyForecastApexChart - mixed bar/line chart with a shaded confidence band.
 */
const DailyForecastApexChart = ({ forecastData, baselineData, onChartContextChange }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsMounted(true), 0);
    return () => clearTimeout(timer);
  }, []);

  const chartConfig = useMemo(() => {
    if (!forecastData || !Array.isArray(forecastData.dates) || forecastData.dates.length === 0) {
      const MIN_DAYS = 30;
      const today = new Date();
      const safeDates = [];
      const baseValues = [];
      const promoValues = [];
      const totalValues = [];

      for (let i = MIN_DAYS - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        safeDates.push(normalizeLabel(d.toISOString().split('T')[0], MIN_DAYS - i - 1, 'Date'));
        baseValues.push(0);
        promoValues.push(0);
        totalValues.push(0);
      }

      const baseSeries = buildXYSeries(safeDates, baseValues);
      const promoSeries = buildXYSeries(safeDates, promoValues);
      const totalSeries = buildXYSeries(safeDates, totalValues);
      const confidenceBand = buildConfidenceBand(safeDates, totalValues, [], []);

      const series = [
        { name: 'Confidence Band', type: 'rangeArea', data: confidenceBand },
        { name: 'Promotional Demand', type: 'bar', data: promoSeries },
        { name: 'Base Demand', type: 'bar', data: baseSeries },
        { name: 'Total Demand', type: 'line', data: totalSeries },
      ];

      return {
        series,
        options: createOptions({
          safeDates,
          baseSeries: baseValues,
          promoSeries: promoValues,
          totalSeries: totalValues,
          confidenceLower: [],
          confidenceUpper: [],
          anomalyFlags: [],
          baselineSeries: [],
          hasBaseline: false,
          seriesCount: series.length,
        }),
      };
    }

    const dates = forecastData.dates.map((date, index) => normalizeLabel(date, index, 'Date'));
    const baseSeriesRaw = Array.isArray(forecastData.base_forecast) ? forecastData.base_forecast : [];
    const promoSeriesRaw = Array.isArray(forecastData.promotional_forecast) ? forecastData.promotional_forecast : [];
    const totalSeriesRaw = Array.isArray(forecastData.total_forecast) ? forecastData.total_forecast : [];
    const confidenceUpperRaw = Array.isArray(forecastData.confidence_upper) ? forecastData.confidence_upper : [];
    const confidenceLowerRaw = Array.isArray(forecastData.confidence_lower) ? forecastData.confidence_lower : [];
    const anomalyFlagsRaw = Array.isArray(forecastData.anomaly_flags) ? forecastData.anomaly_flags : [];
    const anomalyNotesRaw = Array.isArray(forecastData.anomaly_notes) ? forecastData.anomaly_notes : [];
    const anomalyStrengthsRaw = Array.isArray(forecastData.anomaly_strengths) ? forecastData.anomaly_strengths : [];
    const anomalyDirectionsRaw = Array.isArray(forecastData.anomaly_directions) ? forecastData.anomaly_directions : [];
    const anomalyReasonsRaw = Array.isArray(forecastData.anomaly_reasons) ? forecastData.anomaly_reasons : [];

    const maxLen = Math.max(
      dates.length,
      baseSeriesRaw.length,
      promoSeriesRaw.length,
      totalSeriesRaw.length,
      confidenceUpperRaw.length,
      confidenceLowerRaw.length,
      anomalyFlagsRaw.length,
      anomalyNotesRaw.length,
      anomalyStrengthsRaw.length,
      anomalyDirectionsRaw.length,
      anomalyReasonsRaw.length,
      Array.isArray(baselineData) ? baselineData.length : 0
    );

    const safeDates = padToLength(dates, maxLen, '').map((value, index) => {
      if (value) return value;
      const fallbackDate = new Date(forecastData.dates[forecastData.dates.length - 1]);
      fallbackDate.setDate(fallbackDate.getDate() + (index - dates.length + 1));
      return normalizeLabel(fallbackDate.toISOString().split('T')[0], index, 'Date');
    });

    const baseValues = padToLength(baseSeriesRaw.map((v) => toNumber(v) ?? 0), maxLen, 0);
    const promoValues = padToLength(promoSeriesRaw.map((v) => toNumber(v) ?? 0), maxLen, 0);
    const totalValues = padToLength(
      totalSeriesRaw.length > 0
        ? totalSeriesRaw.map((v) => toNumber(v) ?? 0)
        : baseValues.map((value, index) => value + (promoValues[index] ?? 0)),
      maxLen,
      0
    );
    const confidenceUpper = padToLength(confidenceUpperRaw.map((v) => toNumber(v)), maxLen, null);
    const confidenceLower = padToLength(confidenceLowerRaw.map((v) => toNumber(v)), maxLen, null);
    const anomalyDirectionsRawText = anomalyDirectionsRaw.map((v) => (v == null ? '' : String(v)));
    const anomalyReasonsRawText = anomalyReasonsRaw.map((v) => (v == null ? '' : String(v)));

    const backendHasAnomalySignal =
      anomalyFlagsRaw.some((v) => Boolean(v)) ||
      anomalyNotesRaw.some((v) => String(v ?? '').trim().length > 0) ||
      anomalyStrengthsRaw.some((v) => (toNumber(v) ?? 0) !== 0) ||
      anomalyDirectionsRawText.some((v) => String(v ?? '').trim().length > 0) ||
      anomalyReasonsRawText.some((v) => String(v ?? '').trim().length > 0);

  const historicalSummary = forecastData.historical_summary || {};
  const fallbackAnomalies = buildSpikeFallback(totalValues, baseValues, promoValues, historicalSummary);
    const sourceAnomalyNotes = backendHasAnomalySignal ? anomalyNotesRaw : fallbackAnomalies.notes;
    const sourceAnomalyStrengths = backendHasAnomalySignal ? anomalyStrengthsRaw : fallbackAnomalies.strengths;
    const sourceAnomalyDirections = backendHasAnomalySignal ? anomalyDirectionsRawText : fallbackAnomalies.directions;
    const sourceAnomalyReasons = backendHasAnomalySignal ? anomalyReasonsRawText : fallbackAnomalies.reasons;

    const anomalyNotes = padToLength(sourceAnomalyNotes.map((v) => (v == null ? '' : String(v))), maxLen, '');
    const anomalyStrengths = padToLength(sourceAnomalyStrengths.map((v) => toNumber(v) ?? 0), maxLen, 0);
    const anomalyDirections = padToLength(sourceAnomalyDirections.map((v) => (v == null ? '' : String(v))), maxLen, '');
    const anomalyReasons = padToLength(sourceAnomalyReasons.map((v) => (v == null ? '' : String(v))), maxLen, '');

    const histMedAnomaly = toNumber(historicalSummary?.historical_median ?? historicalSummary?.median) ?? 0;

    const anomalyFlags = padToLength(
      backendHasAnomalySignal ? anomalyFlagsRaw : fallbackAnomalies.flags,
      maxLen,
      false
    );

    const topAnomalyIndices = anomalyFlags
      .map((flag, index) => (flag ? index : null))
      .filter((v) => v != null);

    const maxTotal = totalValues.length > 0 ? Math.max(...totalValues) : 0;
    const minTotal = totalValues.length > 0 ? Math.min(...totalValues) : 0;
    const globalHighIndex = totalValues.findIndex((value) => value === maxTotal);
    const globalLowIndex = totalValues.findIndex((value) => value === minTotal);
    const highDeviation = histMedAnomaly > 0 ? (maxTotal - histMedAnomaly) / histMedAnomaly : 0;
    const lowDeviation = histMedAnomaly > 0 ? (histMedAnomaly - minTotal) / histMedAnomaly : 0;
    if (
      globalHighIndex >= 0 &&
      highDeviation >= 0.35 &&
      !topAnomalyIndices.includes(globalHighIndex)
    ) {
      topAnomalyIndices.push(globalHighIndex);
    }
    if (
      globalLowIndex >= 0 &&
      globalLowIndex !== globalHighIndex &&
      lowDeviation >= 0.35 &&
      !topAnomalyIndices.includes(globalLowIndex)
    ) {
      topAnomalyIndices.push(globalLowIndex);
    }

    const displayAnomalySet = new Set(topAnomalyIndices);
    const displayAnomalyFlags = Array.from({ length: maxLen }, (_, index) => displayAnomalySet.has(index));
    const deriveAnomalyDirection = (index) => (index === globalLowIndex ? 'spike_down' : 'spike_up');
    const deriveAnomalyNote = (index) => {
      const existing = String(anomalyNotes[index] ?? '').trim();
      if (existing) return existing;
      const value = totalValues[index] ?? 0;
      const deltaPct = histMedAnomaly > 0 ? Math.abs(((value - histMedAnomaly) / histMedAnomaly) * 100) : 0;
      return deriveAnomalyDirection(index) === 'spike_up'
        ? `Spike detected vs historical norm (${deltaPct.toFixed(1)}% above historical median).`
        : `Drop detected vs historical norm (${deltaPct.toFixed(1)}% below historical median).`;
    };
    const deriveAnomalyReason = (index) => {
      const existing = String(anomalyReasons[index] ?? '').trim();
      if (existing && !isWeakAnomalyReason(existing)) return existing;
      const baseAt = baseValues[index] ?? 0;
      const promoAt = promoValues[index] ?? 0;
      const totalAt = totalValues[index] ?? 0;
      const direction = deriveAnomalyDirection(index);
      const dateText = String(safeDates[index] || '');
      const monthMatch = dateText.match(/^(\d{2})[-/]/) || dateText.match(/^\d{4}[-/](\d{2})[-/]/);
      const month = monthMatch ? Number(monthMatch[1]) : null;
      if (direction === 'spike_up' && promoAt > 0 && baseAt > 0) {
        const promoRatio = (promoAt / Math.max(baseAt, 1)) * 100;
        if (promoRatio >= 20) {
          if ([11, 12].includes(month)) {
            return `Holiday / year-end promotion spike: promotional demand is ${promoRatio.toFixed(1)}% of base demand`;
          }
          if ([5, 6, 7, 8].includes(month)) {
            return `Seasonal promotion spike: promotional demand is ${promoRatio.toFixed(1)}% of base demand`;
          }
          return `Promotion / offer spike: promotional demand is ${promoRatio.toFixed(1)}% of base demand`;
        }
        return 'Promotion uplift contributed to this spike';
      }
      if (direction === 'spike_down' && promoAt <= 0) {
        return 'Demand drop with no promotional support in this period';
      }
      const fallbackReason = String(fallbackAnomalies.reasons?.[index] ?? '').trim();
      if (fallbackReason && !isWeakAnomalyReason(fallbackReason)) return fallbackReason;
      const value = totalAt;
      const localMedian = median(totalValues.slice(Math.max(0, index - 3), Math.min(totalValues.length, index + 4))) ?? histMedAnomaly;
      const deltaPct = localMedian > 0 ? Math.abs(((value - localMedian) / localMedian) * 100) : 0;
      return deltaPct >= 50
        ? 'Sharp seasonal/trend deviation versus nearby demand pattern'
        : 'Noticeable deviation from the surrounding demand pattern';
    };
    const displayAnomalyNotes = displayAnomalyFlags.map((flag, index) => (flag ? deriveAnomalyNote(index) : ''));
    const displayAnomalyStrengths = displayAnomalyFlags.map((flag, index) => {
      if (!flag) return 0;
      const existing = anomalyStrengths[index] ?? 0;
      if (existing > 0) return existing;
      const value = totalValues[index] ?? 0;
      return histMedAnomaly > 0 ? Math.abs(((value - histMedAnomaly) / histMedAnomaly) * 100) : 0;
    });
    const displayAnomalyReasons = displayAnomalyFlags.map((flag, index) => (flag ? deriveAnomalyReason(index) : ''));

    const hasBaseline = Array.isArray(baselineData) && baselineData.length > 0;
    const baselineSeries = hasBaseline
      ? padToLength(baselineData.map((v) => toNumber(v) ?? 0), maxLen, 0)
      : [];

    const baseSeries = buildXYSeries(safeDates, baseValues);
    const promoSeries = buildXYSeries(safeDates, promoValues);
    const totalSeries = buildXYSeries(safeDates, totalValues);
    const baselineXY = hasBaseline ? buildXYSeries(safeDates, baselineSeries) : [];
    const confidenceBand = buildConfidenceBand(safeDates, totalValues, confidenceUpper, confidenceLower);
    const anomalyAnnotationIndices = Array.from(displayAnomalySet).sort((a, b) => a - b);

    const anomalyXAnnotations = anomalyAnnotationIndices.map((index) => {
      const direction = anomalyDirections[index] || (totalValues[index] >= (baselineSeries[index] ?? totalValues[index]) ? 'spike_up' : 'spike_down');
      const isDrop = direction === 'spike_down';
      return {
        x: safeDates[index],
        borderColor: '#EF4444',
        strokeDashArray: 0,
        label: {
          text: isDrop ? 'Drop' : 'Spike',
          offsetY: 0,
          style: {
            color: '#FFFFFF',
            background: '#EF4444',
            fontSize: '10px',
            fontWeight: 700,
          },
        },
      };
    });
    const anomalyPointAnnotations = anomalyAnnotationIndices.map((index) => {
      const direction = anomalyDirections[index] || (totalValues[index] >= (baselineSeries[index] ?? totalValues[index]) ? 'spike_up' : 'spike_down');
      const isDrop = direction === 'spike_down';
      return {
        x: safeDates[index],
        y: totalValues[index] ?? 0,
        marker: {
          size: 7,
          fillColor: '#EF4444',
          strokeColor: '#FFFFFF',
          strokeWidth: 3,
        },
        label: {
          text: isDrop ? 'Drop' : 'Spike',
          borderColor: '#EF4444',
          offsetY: -10,
          style: {
            color: '#FFFFFF',
            background: '#EF4444',
            fontSize: '10px',
            fontWeight: 700,
          },
        },
      };
    });

    const series = [
      { name: 'Base Demand', type: 'bar', data: baseSeries },
      { name: 'Promotional Demand', type: 'bar', data: promoSeries },
      { name: 'Total Demand', type: 'line', data: totalSeries },
      { name: 'Confidence Band', type: 'rangeArea', data: confidenceBand },
    ];

    if (hasBaseline) {
      series.push({ name: 'SMA Baseline', type: 'line', data: baselineXY });
    }

    const anomalyDetails = anomalyAnnotationIndices.map((index) => ({
      date: safeDates[index],
      total: totalValues[index] ?? 0,
      base: baseValues[index] ?? 0,
      promo: promoValues[index] ?? 0,
      anomaly_flag: true,
      anomaly_note: displayAnomalyNotes[index] || '',
      anomaly_strength: displayAnomalyStrengths[index] || 0,
      anomaly_direction: anomalyDirections[index] || deriveAnomalyDirection(index),
      anomaly_reason: displayAnomalyReasons[index] || '',
    }));

    const anomalyCriteria = {
      source: backendHasAnomalySignal ? 'backend' : 'fallback',
      backend_flags_used: backendHasAnomalySignal,
      rule: 'Flag a point if it is at least 30% away from the historical median or more than 3 standard deviations from a rolling 7-point neighborhood. Keep only the strongest non-overlapping anomalies, up to 4 total and at least 5 points apart.',
      thresholds: {
        historical_median_deviation_pct: 30,
        local_window_points: 7,
        local_sigma_threshold: 3,
        max_visible_anomalies: 4,
        min_gap_points: 5,
      },
      note: backendHasAnomalySignal
        ? 'Backend anomaly flags and reasons are being shown on this chart. The fallback rule is used when backend flags are not available.'
        : 'No backend anomaly flags were provided, so the chart used the local fallback rule.',
    };

    const graphSummary = {
      source: 'dashboard_rendered_chart',
      period_count: safeDates.length,
      first_period: safeDates[0],
      last_period: safeDates[safeDates.length - 1],
      total_visible_demand: totalValues.reduce((sum, value) => sum + (toNumber(value) ?? 0), 0),
      total_base_demand: baseValues.reduce((sum, value) => sum + (toNumber(value) ?? 0), 0),
      total_promotional_demand: promoValues.reduce((sum, value) => sum + (toNumber(value) ?? 0), 0),
      promo_days_or_periods: promoValues.filter((value) => (toNumber(value) ?? 0) > 0).length,
      average_visible_demand: totalValues.length
        ? totalValues.reduce((sum, value) => sum + (toNumber(value) ?? 0), 0) / totalValues.length
        : null,
      highest_points: totalValues
        .map((value, index) => ({ date: safeDates[index], total: toNumber(value) ?? 0, base: baseValues[index] ?? 0, promo: promoValues[index] ?? 0 }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 3),
      lowest_points: totalValues
        .map((value, index) => ({ date: safeDates[index], total: toNumber(value) ?? 0, base: baseValues[index] ?? 0, promo: promoValues[index] ?? 0 }))
        .sort((a, b) => a.total - b.total)
        .slice(0, 3),
      baseline_summary: hasBaseline
        ? {
            visible: true,
            label: 'SMA Baseline',
            point_count: baselineSeries.length,
            average_value: baselineSeries.length
              ? baselineSeries.reduce((sum, value) => sum + (toNumber(value) ?? 0), 0) / baselineSeries.length
              : null,
            min_value: baselineSeries.length ? Math.min(...baselineSeries.map((value) => toNumber(value) ?? 0)) : null,
            max_value: baselineSeries.length ? Math.max(...baselineSeries.map((value) => toNumber(value) ?? 0)) : null,
            first_value: baselineSeries[0] ?? null,
            last_value: baselineSeries[baselineSeries.length - 1] ?? null,
          }
        : {
            visible: false,
            label: 'SMA Baseline',
            point_count: 0,
          },
      anomaly_criteria: anomalyCriteria,
      anomaly_details: anomalyDetails,
    };

    return {
      series,
      graph_summary: graphSummary,
      options: createOptions({
        safeDates,
        baseSeries: baseValues,
        promoSeries: promoValues,
        totalSeries: totalValues,
        confidenceLower,
        confidenceUpper,
        anomalyFlags: displayAnomalyFlags,
        anomalyNotes: displayAnomalyNotes,
        anomalyStrengths: displayAnomalyStrengths,
        anomalyXAnnotations,
        anomalyPointAnnotations,
        anomalyReasons: displayAnomalyReasons,
        baselineSeries,
        hasBaseline,
        seriesCount: series.length,
      }),
    };
  }, [forecastData, baselineData]);

  useEffect(() => {
    if (typeof onChartContextChange === 'function') {
      onChartContextChange(chartConfig?.graph_summary ? { graph_summary: chartConfig.graph_summary } : null);
    }
  }, [chartConfig, onChartContextChange]);

  if (!chartConfig || !isMounted) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>Chart</div>
        <p style={{ fontWeight: 600 }}>No forecast data to display</p>
        <p style={{ fontSize: '0.875rem' }}>Configure parameters and run a forecast to see results</p>
      </div>
    );
  }

  return (
    <div className="apex-chart-wrapper">
      <ReactApexChart
        options={chartConfig.options}
        series={chartConfig.series}
        type="line"
        height={420}
      />
    </div>
  );
};

const createOptions = ({
  safeDates,
  baseSeries,
  promoSeries,
  totalSeries,
  confidenceLower,
  confidenceUpper,
  anomalyFlags,
  anomalyNotes,
  anomalyStrengths,
  anomalyXAnnotations,
  anomalyPointAnnotations,
  anomalyReasons,
  baselineSeries,
  hasBaseline,
  seriesCount,
}) => ({
  chart: {
    id: 'daily-forecast-main-chart',
    type: 'bar',
    stacked: false,
    stackOnlyBar: false,
    height: 420,
    toolbar: {
      show: true,
      autoSelected: 'zoom',
      tools: {
        download: true,
        selection: false,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: false,
        reset: true,
      },
    },
    zoom: { enabled: true },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 600,
      animateGradually: { enabled: true, delay: 80 },
      dynamicAnimation: { enabled: true, speed: 350 },
    },
    fontFamily: 'Inter, -apple-system, sans-serif',
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '34%',
      borderRadius: 4,
      borderRadiusApplication: 'end',
      borderRadiusWhenStacked: 'all',
    },
  },
  colors: ['#0057B8', '#8B5CF6', '#10B981', '#34D399'].slice(0, seriesCount),
  fill: {
    type: Array(seriesCount).fill('solid'),
    opacity: [0.95, 0.95, 1, 0.14].slice(0, seriesCount),
  },
  stroke: {
    width: [0, 0, 2.4, 0].slice(0, seriesCount),
    curve: 'smooth',
    dashArray: [0, 0, 6, 0].slice(0, seriesCount),
  },
  markers: {
    size: 0,
    colors: ['#10B981'],
    strokeColors: '#fff',
    strokeWidth: 2,
    hover: { size: 5 },
    discrete: (anomalyFlags || [])
      .map((flag, index) => (flag ? {
        seriesIndex: 2,
        dataPointIndex: index,
        fillColor: '#EF4444',
        strokeColor: '#FFFFFF',
        size: 7,
        strokeWidth: 3,
        shape: 'circle',
      } : null))
      .filter(Boolean),
  },
  xaxis: {
    type: 'category',
    categories: safeDates,
    labels: {
      rotate: -30,
      rotateAlways: safeDates.length > 20,
      style: { fontSize: '11px', fontFamily: 'Inter, sans-serif', colors: '#64748B' },
      formatter: (val) => {
        const text = String(val ?? '').trim();
        if (!text) return '';
        return text.length > 7 ? text.slice(5) : text;
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tickAmount: Math.min(safeDates.length, 12),
  },
  yaxis: {
    title: { text: 'Demand Units', style: { fontSize: '12px', fontWeight: 600, color: '#64748B' } },
    labels: {
      style: { fontSize: '11px', colors: '#64748B' },
      formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}K` : Math.round(val).toLocaleString()),
    },
  },
  tooltip: {
    theme: 'light',
    style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
    custom: ({ dataPointIndex }) => {
      const index = Number(dataPointIndex);
      if (!Number.isFinite(index) || index < 0 || index >= safeDates.length) return '';
      const base = safeSeriesValue(baseSeries, index, 0);
      const promo = safeSeriesValue(promoSeries, index, 0);
      const total = safeSeriesValue(totalSeries, index, base + promo);
      const low = safeSeriesValue(confidenceLower, index, 0);
      const high = safeSeriesValue(confidenceUpper, index, 0);
      const isAnomaly = Boolean(anomalyFlags?.[index]);
      const note = String(anomalyNotes?.[index] ?? '').trim();
      const reason = String(anomalyReasons?.[index] ?? '').trim();
      const strength = safeSeriesValue(anomalyStrengths, index, 0);
      const baseline = hasBaseline ? safeSeriesValue(baselineSeries, index, 0) : null;
      const badge = isAnomaly
        ? `<div style="display:inline-flex;align-items:center;gap:6px;margin-top:6px;padding:4px 8px;border-radius:999px;background:#FEE2E2;color:#B91C1C;font-size:11px;font-weight:700;">Anomaly</div>`
        : '';
      const noteHtml = note
        ? `<div style="margin-top:8px;color:#475569;font-size:12px;line-height:1.35;">${note}</div>`
        : '';
      const reasonHtml = reason
        ? `<div style="margin-top:4px;color:#B91C1C;font-size:12px;line-height:1.35;"><strong>Reason:</strong> ${reason}</div>`
        : '';
      const strengthHtml = isAnomaly && Number.isFinite(strength) && strength > 0
        ? `<div style="margin-top:4px;color:#475569;font-size:12px;line-height:1.35;"><strong>Strength:</strong> ${strength.toFixed(1)}%</div>`
        : '';
      const rows = [
        `<div style="display:flex;justify-content:space-between;gap:12px;color:#0F172A;font-size:12px;"><span style="font-weight:600;">Confidence Band</span><span>${formatTooltipValue([low, high])}</span></div>`,
        `<div style="display:flex;justify-content:space-between;gap:12px;color:#0F172A;font-size:12px;"><span style="font-weight:600;">Base Demand</span><span>${formatTooltipValue(base)}</span></div>`,
        `<div style="display:flex;justify-content:space-between;gap:12px;color:#0F172A;font-size:12px;"><span style="font-weight:600;">Promotional Demand</span><span>${formatTooltipValue(promo)}</span></div>`,
        `<div style="display:flex;justify-content:space-between;gap:12px;color:#0F172A;font-size:12px;"><span style="font-weight:600;">Total Demand</span><span>${formatTooltipValue(total)}</span></div>`,
      ];
      if (hasBaseline && baseline != null) {
        rows.push(`<div style="display:flex;justify-content:space-between;gap:12px;color:#0F172A;font-size:12px;"><span style="font-weight:600;">SMA Baseline</span><span>${formatTooltipValue(baseline)}</span></div>`);
      }
      return `
        <div style="padding:10px 12px;min-width:240px;background:#fff;border:1px solid #E2E8F0;border-radius:12px;box-shadow:0 10px 24px rgba(15,23,42,0.12);">
          <div style="font-size:13px;font-weight:700;color:#1E293B;margin-bottom:8px;">${safeDates[index] ?? ''}</div>
          ${rows.join('<div style="height:6px"></div>')}
          ${badge}
          ${noteHtml}
          ${reasonHtml}
          ${strengthHtml}
        </div>
      `;
    },
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'center',
    offsetY: 2,
    fontSize: '13px',
    fontFamily: 'Inter, sans-serif',
    markers: { width: 10, height: 10, radius: 999 },
    onItemClick: { toggleDataSeries: true },
  },
  annotations: {
    xaxis: anomalyXAnnotations || [],
    points: anomalyPointAnnotations || [],
  },
  grid: {
    borderColor: '#E2E8F0',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 0, right: 12, bottom: 0, left: 8 },
  },
  dataLabels: { enabled: false },
  responsive: [
    {
      breakpoint: 768,
      options: {
        chart: { height: 300 },
        legend: { position: 'bottom' },
      },
    },
  ],
});

export default DailyForecastApexChart;
